import { ChevronDown } from 'lucide-react';
import { DevicesItem } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DevicesTab/DevicesItem';
import type { DevicesSectionProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DevicesTab/DevicesSection/index.types';
import {
  SURFACE,
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import React, { useState } from 'react';
import { cn } from '~/lib/utils';

/**
 * DevicesSection - Collapsible list of devices of one kind (mic, speaker, ringer)
 *
 * The collapsed header carries the currently selected device, so the
 * question the section exists to answer — "what is the call actually using?"
 * — is answered without opening it.
 */
export const DevicesSection: React.FC<DevicesSectionProps> = ({
  title,
  icon,
  devices,
  selectedId,
  defaultExpanded = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const selected = devices.find((device) => device.deviceId === selectedId);
  // A selectedId with no matching device means the stored choice outlived the
  // device — unplugged, or hidden because the microphone was never granted.
  const selectedLabel =
    selected?.label ||
    (selectedId ? 'Selected device not reported' : 'None selected');
  const tone = selected ? TONE.ok : TONE.muted;

  return (
    <div className={SURFACE.card}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        className="flex w-full items-center gap-[9px] px-3 py-2.5 text-left transition-colors hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-400/40"
      >
        <span className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center text-zinc-400">
          {icon}
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-[7px]">
            <span className="text-[12.5px] font-semibold text-zinc-200">
              {title}
            </span>
            <span className="rounded bg-zinc-800 px-[5px] py-px font-sp-mono text-[10px] text-zinc-400">
              {devices.length}
            </span>
          </span>
          <span className="mt-0.5 flex items-center gap-1.5">
            <span
              className={cn('h-[5px] w-[5px] flex-shrink-0 rounded-full', tone.dot)}
            />
            <span
              className={cn(
                'truncate text-[11.5px]',
                selected ? 'text-zinc-300' : 'text-zinc-400'
              )}
            >
              {selectedLabel}
            </span>
          </span>
        </span>

        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0 text-zinc-300 transition-transform',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {isExpanded && (
        <div className="border-t border-zinc-800">
          {devices.length > 0 ? (
            devices.map((device) => (
              <DevicesItem
                key={device.deviceId}
                device={device}
                isSelected={device.deviceId === selectedId}
              />
            ))
          ) : (
            <p className="px-3 py-3 text-center text-[11.5px] text-zinc-400">
              None reported by the browser
            </p>
          )}
        </div>
      )}
    </div>
  );
};
